import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Calendar, Check, Clock, FileText, Mail, Phone, Upload, User, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Public resume review booking: name, email, phone and a resume, then one of
// the 30-minute slots from 3 to 6 PM on the next working days. Saved on the
// Railway server (server/routes/resumeReview.js), which emails the person and
// admin and shows the booking on the tracker's Resume Reviews page.
const API_BASE = 'https://vistatech-serversss-production.up.railway.app';
const MAX_BYTES = 5 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SlotInfo { slot: string; label: string; state: 'free' | 'booked' | 'closed' }
interface DayInfo { date: string; label: string; free: number; slots: SlotInfo[] }
interface Booking { name: string; email: string; phone: string; dateLabel: string; timeLabel: string }

const phone10 = (s: string) => s.replace(/\D/g, '').slice(-10);
const istToday = () => new Date(Date.now() + 330 * 60 * 1000).toISOString().slice(0, 10);
const fmtSize = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(new Error('Could not read the file'));
  reader.readAsDataURL(file);
});

// ── Styles (the same dark glass look as the group enrollment form) ───────────
const inp = (err?: string): React.CSSProperties => ({
  width: '100%', padding: '11px 12px 11px 36px', borderRadius: '10px',
  border: `1.5px solid ${err ? '#f87171' : 'rgba(255,255,255,0.1)'}`,
  background: err ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.05)',
  color: 'white', fontSize: '0.88rem', fontFamily: 'Poppins, sans-serif', outline: 'none', boxSizing: 'border-box',
});
const lbl: React.CSSProperties = {
  display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 700,
  marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.08em',
};
const errTxt: React.CSSProperties = { color: '#f87171', fontSize: '0.72rem', marginTop: '5px', fontWeight: 600 };
const glassCard: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '22px', padding: 'clamp(20px, 5vw, 32px)', backdropFilter: 'blur(24px)',
};
const iconAt: React.CSSProperties = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' };
const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  width: '100%', padding: '13px', borderRadius: '12px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '0.92rem', color: '#0d0b14',
  background: 'linear-gradient(135deg, #F0B429, #FF6535)', opacity: disabled ? 0.55 : 1,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
});
const spinner = <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(13,11,20,0.25)', borderTopColor: '#0d0b14', animation: 'rr-spin .8s linear infinite', display: 'inline-block' }} />;

const Orbs = () => (
  <>
    {[
      { s: 500, t: '-180px', l: '-150px', c: 'rgba(78,58,105,0.45)' },
      { s: 380, b: '-120px', r: '-80px', c: 'rgba(240,180,41,0.12)' },
      { s: 260, t: '45%', l: '58%', c: 'rgba(255,101,53,0.1)' },
    ].map((o: any, i) => (
      <motion.div key={i}
        style={{ position: 'absolute', width: o.s, height: o.s, borderRadius: '50%', background: `radial-gradient(circle, ${o.c}, transparent 70%)`,
          top: o.t, left: o.l, right: o.r, bottom: o.b, pointerEvents: 'none' }}
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 10 + i * 4, repeat: Infinity, ease: 'easeInOut' }} />
    ))}
  </>
);

const Field: React.FC<{ label: string; icon: React.ReactNode; error?: string; children: React.ReactNode }> = ({ label, icon, error, children }) => (
  <div>
    <label style={lbl}>{label}</label>
    <div style={{ position: 'relative' }}>
      <span style={iconAt}>{icon}</span>
      {children}
    </div>
    {error && <p style={errTxt}>{error}</p>}
  </div>
);

const ResumeReview: React.FC = () => {
  const [step, setStep] = useState<'details' | 'slot' | 'done'>('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const [days, setDays] = useState<DayInfo[] | null>(null);
  const [slotsError, setSlotsError] = useState('');
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [booking, setBooking] = useState<Booking | null>(null);

  const loadSlots = useCallback(async (keep?: { date: string; slot: string }) => {
    setSlotsError('');
    try {
      const res = await fetch(`${API_BASE}/api/resume-review-slots`);
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Could not load the slots');
      const list: DayInfo[] = data.days || [];
      setDays(list);
      // Keep the choice if it's still free, else the first day with a free slot
      const day = list.find(d => d.date === keep?.date) || list.find(d => d.free > 0) || list[0];
      setDate(day?.date || '');
      setSlot(day && keep && day.date === keep.date && day.slots.some(s => s.slot === keep.slot && s.state === 'free') ? keep.slot : '');
    } catch (e: any) {
      setSlotsError(e.message === 'Failed to fetch' ? 'Could not reach the server. Check your connection and try again.' : e.message);
    }
  }, []);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  // An error goes away as soon as that field is edited
  const edit = (field: string, set: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    set(e.target.value);
    if (errors[field]) setErrors(x => ({ ...x, [field]: '' }));
  };

  const pickFile = (f: File | undefined) => {
    if (!f) return;
    const ext = f.name.toLowerCase().split('.').pop() || '';
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      setErrors(e => ({ ...e, resume: 'Please upload a PDF or Word file (.pdf, .doc, .docx)' }));
    } else if (f.size > MAX_BYTES) {
      setErrors(e => ({ ...e, resume: `This file is ${fmtSize(f.size)}. Please upload one under 5 MB.` }));
    } else {
      setResume(f);
      setErrors(e => ({ ...e, resume: '' }));
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const nextStep = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Please enter your name';
    if (!EMAIL_RE.test(email.trim())) e.email = 'Please enter a valid email address';
    if (!/^[6-9]\d{9}$/.test(phone10(phone))) e.phone = 'Please enter a valid 10-digit mobile number';
    if (!resume) e.resume = 'Please upload your resume';
    setErrors(e);
    if (Object.values(e).some(Boolean)) return;
    setSubmitError('');
    setStep('slot');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirm = async () => {
    if (!resume || !date || !slot || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const data64 = await fileToBase64(resume);
      const res = await fetch(`${API_BASE}/api/resume-review-book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), phone: phone10(phone), date, slot, resume: { data: data64, fileName: resume.name } }),
      });
      const data = await res.json().catch(() => null);
      if (!data?.ok) throw new Error(data?.error || 'Something went wrong. Please try again.');
      setBooking(data.booking);
      setStep('done');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setSubmitError(e.message === 'Failed to fetch' ? 'Could not reach the server. Check your connection and try again.' : e.message);
      loadSlots({ date, slot });   // someone may have taken it
    } finally {
      setSubmitting(false);
    }
  };

  const today = istToday();
  const tomorrow = new Date(Date.parse(`${today}T00:00:00Z`) + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const day = days?.find(d => d.date === date);
  const chosen = day?.slots.find(s => s.slot === slot);

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg, #2b2240 0%, #0d0b14 60%, #1a0b24 100%)', position: 'relative', overflow: 'hidden' }}>
      <Orbs />
      <style>{`
        @keyframes rr-spin { to { transform: rotate(360deg); } }
        .rr-days { scrollbar-width: none; } .rr-days::-webkit-scrollbar { display: none; }
        .rr-input::placeholder { color: rgba(255,255,255,0.28); }
        .rr-input:focus { border-color: rgba(240,180,41,0.6) !important; }
      `}</style>

      <div style={{ position: 'relative', zIndex: 1, padding: '48px 16px 80px' }}>
        {/* Heading */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.25)', marginBottom: '16px' }}>
            <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }}
              style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F0B429', display: 'inline-block' }} />
            <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#F0B429', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Vista Tech Placement Team</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ fontSize: 'clamp(1.9rem, 5vw, 2.8rem)', fontWeight: 900, color: 'white', lineHeight: 1.15, margin: '0 0 10px' }}>
            Book Your{' '}
            <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Resume Review</span>
          </motion.h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', margin: 0 }}>
            Upload your resume and pick a 30-minute slot between 3 and 6 PM
          </p>
        </div>

        <div style={{ maxWidth: '540px', margin: '0 auto' }}>
          {/* Steps */}
          {step !== 'done' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '18px' }}>
              {(['details', 'slot'] as const).map((s, i) => {
                const active = step === s, past = step === 'slot' && s === 'details';
                return (
                  <React.Fragment key={s}>
                    {i > 0 && <span style={{ width: 28, height: 1, background: 'rgba(255,255,255,0.15)' }} />}
                    <span style={{ display: 'flex', alignItems: 'center', gap: '7px', color: active ? '#F0B429' : past ? '#34d399' : 'rgba(255,255,255,0.35)', fontSize: '0.78rem', fontWeight: 700 }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem',
                        background: active ? 'rgba(240,180,41,0.15)' : past ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.06)',
                        border: `1px solid ${active ? 'rgba(240,180,41,0.5)' : past ? 'rgba(52,211,153,0.5)' : 'rgba(255,255,255,0.12)'}` }}>
                        {past ? <Check size={12} /> : i + 1}
                      </span>
                      {s === 'details' ? 'Your details' : 'Pick a slot'}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* ── Step 1: details + resume ── */}
            {step === 'details' && (
              <motion.div key="details" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} style={glassCard}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Field label="Full name" icon={<User size={15} />} error={errors.name}>
                    <input className="rr-input" id="rr-name" value={name} onChange={edit('name', setName)} placeholder="Your full name" autoComplete="name" style={inp(errors.name)} />
                  </Field>
                  <Field label="Email" icon={<Mail size={15} />} error={errors.email}>
                    <input className="rr-input" id="rr-email" type="email" value={email} onChange={edit('email', setEmail)} placeholder="you@example.com" autoComplete="email" style={inp(errors.email)} />
                  </Field>
                  <Field label="Mobile number" icon={<Phone size={15} />} error={errors.phone}>
                    <input className="rr-input" id="rr-phone" type="tel" inputMode="tel" value={phone} onChange={edit('phone', setPhone)} placeholder="10-digit mobile number" autoComplete="tel" style={inp(errors.phone)} />
                  </Field>

                  <div>
                    <label style={lbl}>Resume</label>
                    <input ref={fileRef} id="rr-file" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={e => pickFile(e.target.files?.[0])} style={{ display: 'none' }} />
                    {resume ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', borderRadius: '12px', background: 'rgba(52,211,153,0.07)', border: '1.5px solid rgba(52,211,153,0.35)' }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(52,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <FileText size={18} color="#34d399" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ color: 'white', fontSize: '0.85rem', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resume.name}</p>
                          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: 0 }}>{fmtSize(resume.size)}</p>
                        </div>
                        <button type="button" onClick={() => fileRef.current?.click()}
                          style={{ background: 'none', border: 'none', color: '#F0B429', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Poppins, sans-serif' }}>Change</button>
                        <button type="button" aria-label="Remove file" onClick={() => setResume(null)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 2, display: 'flex' }}><X size={15} /></button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileRef.current?.click()}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
                        style={{ width: '100%', padding: '22px 14px', borderRadius: '12px', cursor: 'pointer', fontFamily: 'Poppins, sans-serif',
                          background: errors.resume ? 'rgba(248,113,113,0.06)' : 'rgba(255,255,255,0.03)',
                          border: `1.5px dashed ${errors.resume ? '#f87171' : 'rgba(255,255,255,0.18)'}`,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <Upload size={22} color="#F0B429" />
                        <span style={{ color: 'white', fontSize: '0.85rem', fontWeight: 600 }}>Upload your resume</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}>PDF or Word, up to 5 MB</span>
                      </button>
                    )}
                    {errors.resume && <p style={errTxt}>{errors.resume}</p>}
                  </div>

                  <motion.button type="button" whileTap={{ scale: 0.98 }} onClick={nextStep} style={{ ...primaryBtn(false), marginTop: '4px' }}>
                    Next: pick a slot
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* ── Step 2: date + slot ── */}
            {step === 'slot' && (
              <motion.div key="slot" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                <button type="button" onClick={() => setStep('details')}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', fontFamily: 'Poppins, sans-serif', fontWeight: 600, marginBottom: '12px', padding: 0 }}>
                  <ArrowLeft size={13} /> Back to your details
                </button>
                <div style={glassCard}>
                  {slotsError ? (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                      <p style={{ color: '#fca5a5', fontSize: '0.85rem', margin: '0 0 12px' }}>{slotsError}</p>
                      <button type="button" onClick={() => loadSlots()} style={{ ...primaryBtn(false), width: 'auto', padding: '10px 22px', margin: '0 auto' }}>Try again</button>
                    </div>
                  ) : !days ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
                      <span style={{ width: 26, height: 26, borderRadius: '50%', border: '3px solid rgba(240,180,41,0.2)', borderTopColor: '#F0B429', animation: 'rr-spin .8s linear infinite' }} />
                    </div>
                  ) : days.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem', textAlign: 'center', margin: '12px 0' }}>No dates are open right now. Please check again later.</p>
                  ) : (
                    <>
                      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={13} /> Pick a date</label>
                      <div className="rr-days" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '20px' }}>
                        {days.map(d => {
                          const [wd, ...rest] = d.label.split(' ');
                          const sel = d.date === date, full = d.free === 0;
                          const top = d.date === today ? 'Today' : d.date === tomorrow ? 'Tomorrow' : wd;
                          return (
                            <button key={d.date} type="button" data-date={d.date} onClick={() => { setDate(d.date); setSlot(''); setSubmitError(''); }}
                              style={{ flexShrink: 0, minWidth: '78px', padding: '10px 8px', borderRadius: '12px', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', textAlign: 'center',
                                background: sel ? 'linear-gradient(135deg, rgba(240,180,41,0.22), rgba(255,101,53,0.18))' : 'rgba(255,255,255,0.04)',
                                border: `1.5px solid ${sel ? 'rgba(240,180,41,0.7)' : 'rgba(255,255,255,0.1)'}`, opacity: full && !sel ? 0.55 : 1 }}>
                              <span style={{ display: 'block', color: sel ? '#F0B429' : 'rgba(255,255,255,0.5)', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase' }}>{top}</span>
                              <span style={{ display: 'block', color: 'white', fontSize: '0.9rem', fontWeight: 800, margin: '2px 0' }}>{rest.join(' ')}</span>
                              <span style={{ display: 'block', color: full ? '#f87171' : '#34d399', fontSize: '0.65rem', fontWeight: 600 }}>{full ? 'Full' : `${d.free} free`}</span>
                            </button>
                          );
                        })}
                      </div>

                      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Clock size={13} /> Pick a time</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                        {day?.slots.map(s => {
                          const sel = s.slot === slot, free = s.state === 'free';
                          return (
                            <button key={s.slot} type="button" data-slot={s.slot} disabled={!free} onClick={() => { setSlot(s.slot); setSubmitError(''); }}
                              style={{ padding: '12px 8px', borderRadius: '12px', fontFamily: 'Poppins, sans-serif', cursor: free ? 'pointer' : 'not-allowed', textAlign: 'center',
                                background: sel ? 'linear-gradient(135deg, #F0B429, #FF6535)' : free ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                                border: `1.5px solid ${sel ? 'transparent' : free ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)'}` }}>
                              <span style={{ display: 'block', color: sel ? '#0d0b14' : free ? 'white' : 'rgba(255,255,255,0.3)', fontSize: '0.86rem', fontWeight: 700, textDecoration: free ? 'none' : 'line-through' }}>{s.label}</span>
                              {!free && <span style={{ display: 'block', color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', marginTop: '2px' }}>{s.state === 'booked' ? 'Booked' : 'Closed'}</span>}
                            </button>
                          );
                        })}
                      </div>
                      {day && day.free === 0 && (
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', margin: '10px 0 0' }}>Every slot on this day is taken. Please pick another date.</p>
                      )}

                      <div style={{ marginTop: '20px', padding: '14px', borderRadius: '12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <p style={{ ...lbl, marginBottom: '4px' }}>Your booking</p>
                        <p style={{ color: 'white', fontSize: '0.86rem', margin: 0 }}>
                          {name.trim()} · {chosen ? <b style={{ color: '#F0B429' }}>{day?.label}, {chosen.label}</b> : <span style={{ color: 'rgba(255,255,255,0.4)' }}>pick a time above</span>}
                        </p>
                      </div>

                      {submitError && (
                        <div style={{ marginTop: '12px', padding: '11px 13px', borderRadius: '10px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.35)', color: '#fca5a5', fontSize: '0.8rem', lineHeight: 1.5 }}>
                          {submitError}
                        </div>
                      )}

                      <motion.button type="button" id="rr-confirm" whileTap={{ scale: 0.98 }} onClick={confirm} disabled={!chosen || submitting}
                        style={{ ...primaryBtn(!chosen || submitting), marginTop: '16px' }}>
                        {submitting ? <>{spinner} Booking your slot…</> : 'Confirm booking'}
                      </motion.button>
                    </>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── Done ── */}
            {step === 'done' && booking && (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} style={{ ...glassCard, textAlign: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <Check size={30} color="white" />
                </div>
                <h2 style={{ color: 'white', fontWeight: 900, fontSize: '1.35rem', margin: '0 0 6px' }}>Your slot is booked!</h2>
                <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', margin: '0 0 20px' }}>
                  A confirmation has been sent to <b style={{ color: 'white' }}>{booking.email}</b>
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '18px', textAlign: 'left' }}>
                  {[['Date', booking.dateLabel], ['Time', booking.timeLabel]].map(([k, v]) => (
                    <div key={k} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '12px 14px' }}>
                      <p style={{ ...lbl, marginBottom: '3px' }}>{k}</p>
                      <p style={{ color: '#F0B429', fontSize: '0.95rem', fontWeight: 800, margin: 0 }}>{v}</p>
                    </div>
                  ))}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', lineHeight: 1.6, margin: 0 }}>
                  Please keep your phone ({booking.phone}) free at this time — our placement team will contact you on it.
                  To change your slot, call or WhatsApp us on <a href="tel:+919894447857" style={{ color: '#F0B429', fontWeight: 700 }}>+91 98944 47857</a>.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ResumeReview;
